import { Fragment, useState, type ReactElement } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/shared/SortableTableHead';
import { SimplePagination } from '@/components/shared/SimplePagination';
import { SeverityBadge } from '@/components/policylist/components/SeverityBadge';
import { RelativeTimeWithTooltip } from '@/components/shared/RelativeTimeWithTooltip';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ChevronDown, ChevronRight, List } from 'lucide-react';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import {
  type GroupedAlert,
  OrganizationScanAlertsSortField,
} from '@kirin/api-client/contracts/alerts';
import {
  formatAlertType,
  formatSubPolicyLabel,
} from '@kirin/api-client/helpers/alerts';
import { getAlertFileDisplayLabel } from '@kirin/api-client/contracts';
import { MiddleTruncatedPath } from '@/components/shared/MiddleTruncatedPath';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { ALERTS_PAGE_SIZE } from './constants';
import { AlertDetailCard } from './AlertDetailCard';
import { AlertUsersDialog } from './AlertUsersDialog';
import { UsersCell } from './UsersCell';

/** Display-only column keys (no sort). */
export const FILE_PATH_FIELD = 'filePath' as const;
export const USERS_FIELD = 'users' as const;

export interface AlertsTableColumn<T extends string> {
  field: T | typeof FILE_PATH_FIELD | typeof USERS_FIELD;
  label: string;
  /** When false, column is display-only (e.g. File). Default true. */
  sortable?: boolean;
}

export interface AlertsTableProps<T extends string> {
  alerts: GroupedAlert[];
  pagination: { total: number; hasMore: boolean };
  page: number;
  onPrevious: () => void;
  onNext: () => void;
  sortBy: T;
  sortOrder: SortOrder;
  onSort: (field: T) => void;
  columns: AlertsTableColumn<T>[];
  /** Base path for building occurrences URL (e.g. /dashboard or /:organizationId). */
  alertsBasePath: string;
}

function renderCell(
  field: string,
  alert: GroupedAlert,
  options: {
    onUsersClick?: (e: React.MouseEvent) => void;
    revampEnabled?: boolean;
  }
): React.ReactNode {
  const { onUsersClick, revampEnabled } = options;
  switch (field) {
    case OrganizationScanAlertsSortField.LastSeenAt:
    case 'lastSeenAt':
      return (
        <RelativeTimeWithTooltip date={alert.lastSeenAt} showIcon={false} />
      );
    case USERS_FIELD:
      return <UsersCell alert={alert} onClick={onUsersClick} />;
    case FILE_PATH_FIELD: {
      // KIR-2563: width-measured middle truncation, keeping the filename visible
      // and the full path copyable. The resolved, human-readable label is used
      // so nothing exposes the raw internal sentinel
      // (e.g. platform-secret:claude:PreToolUse).
      const fileLabel = getAlertFileDisplayLabel({
        filePath: alert.filePath,
        location: alert.location,
        eventKind: alert.eventKind,
      });
      // KIR-3810: alert File cells also get a one-click copy button.
      return <MiddleTruncatedPath path={fileLabel} showInlineCopy />;
    }
    case OrganizationScanAlertsSortField.Type:
    case 'type':
      // KIR-2381: keep this in lock-step with the shared `TypeCell` and
      // `AlertDetailCard` so the row label and the expanded card label
      // never disagree when the flag is on.
      return (
        <Badge variant="outline">
          {revampEnabled
            ? formatSubPolicyLabel(alert.subPolicyKey, alert.type)
            : formatAlertType(alert.type)}
        </Badge>
      );
    case OrganizationScanAlertsSortField.Severity:
    case 'severity':
      return <SeverityBadge riskLevel={alert.severity} showIcon />;
    case OrganizationScanAlertsSortField.OccurrenceCount:
    case 'occurrenceCount':
      return <span className="text-sm">{alert.occurrenceCount ?? '—'}</span>;
    case OrganizationScanAlertsSortField.Score:
    case 'score':
      return <span className="text-sm">{alert.score ?? '—'}</span>;
    case OrganizationScanAlertsSortField.FirstSeenAt:
    case 'firstSeenAt':
      return (
        <RelativeTimeWithTooltip date={alert.firstSeenAt} showIcon={false} />
      );
    default:
      return null;
  }
}

function AlertRow({
  alert,
  columns,
  alertsBasePath,
  onOpenUsersDialog,
}: {
  alert: GroupedAlert;
  columns: AlertsTableColumn<string>[];
  alertsBasePath: string;
  onOpenUsersDialog: (alert: GroupedAlert) => void;
}): ReactElement {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const showUserCount = columns.some(c => (c.field as string) === USERS_FIELD);
  const colSpan = 1 + columns.length;
  // KIR-2381: hoist the flag read here (hooks must run unconditionally) and
  // thread `revampEnabled` into the non-React `renderCell` so row + expanded
  // labels stay aligned.
  const { flags } = useFeatureFlags([flagsmithFeatureFlags.ALERTS_REVAMP]);
  const revampEnabled = flags[flagsmithFeatureFlags.ALERTS_REVAMP] === true;

  const occurrencesPath = `${alertsBasePath}/alerts/${encodeURIComponent(alert.alertKey)}/occurrences`;
  const occurrencesUrl = searchParams.toString()
    ? `${occurrencesPath}?${searchParams.toString()}`
    : occurrencesPath;

  return (
    <>
      <TableRow
        className="cursor-pointer hover:bg-muted/50"
        onClick={() => setExpanded(prev => !prev)}
      >
        <TableCell className="w-8">
          <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
            {expanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </Button>
        </TableCell>
        {columns.map(col => (
          <TableCell
            key={col.field}
            className={
              col.field === USERS_FIELD ? 'text-sm text-center' : undefined
            }
            onClick={
              col.field === USERS_FIELD ? e => e.stopPropagation() : undefined
            }
          >
            {renderCell(col.field as string, alert, {
              onUsersClick: showUserCount
                ? (e: React.MouseEvent) => {
                    e.stopPropagation();
                    onOpenUsersDialog(alert);
                  }
                : undefined,
              revampEnabled,
            })}
          </TableCell>
        ))}
      </TableRow>
      {expanded && (
        <TableRow>
          <TableCell
            colSpan={colSpan}
            className="bg-muted/30 p-4 min-w-0 max-w-full w-full whitespace-normal"
          >
            <div className="min-w-0 max-w-full">
              <AlertDetailCard alert={alert} />
              <div className="mt-2 flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={e => {
                    e.stopPropagation();
                    navigate(occurrencesUrl);
                  }}
                >
                  <List className="h-4 w-4 mr-2" />
                  Show Occurrences
                </Button>
              </div>
            </div>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

/**
 * Shared table for grouped alerts (user or organization).
 * Column order and labels are driven by columns; display-only columns use sortable: false.
 */
export function AlertsTable<T extends string>({
  alerts,
  pagination,
  page,
  onPrevious,
  onNext,
  sortBy,
  sortOrder,
  onSort,
  columns,
  alertsBasePath,
}: AlertsTableProps<T>): ReactElement {
  const [usersDialogAlert, setUsersDialogAlert] = useState<GroupedAlert | null>(
    null
  );

  return (
    <>
      <div className="rounded-md border">
        <Table className="table-fixed">
          <TableHeader>
            <TableRow>
              <TableHead className="w-8" />
              {columns.map(col => (
                <Fragment key={col.field}>
                  {col.sortable === false ? (
                    <TableHead>{col.label}</TableHead>
                  ) : (
                    <SortableTableHead
                      field={col.field as T}
                      currentSortBy={sortBy}
                      currentSortOrder={sortOrder}
                      onSort={onSort}
                    >
                      {col.label}
                    </SortableTableHead>
                  )}
                </Fragment>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {alerts.map(alert => (
              <AlertRow
                key={alert.alertKey}
                alert={alert}
                columns={columns}
                alertsBasePath={alertsBasePath}
                onOpenUsersDialog={setUsersDialogAlert}
              />
            ))}
          </TableBody>
        </Table>
      </div>
      {usersDialogAlert && (
        <AlertUsersDialog
          alert={usersDialogAlert}
          open
          onClose={() => setUsersDialogAlert(null)}
        />
      )}
      <SimplePagination
        page={page}
        limit={ALERTS_PAGE_SIZE}
        total={pagination.total}
        hasMore={pagination.hasMore}
        onPrevious={onPrevious}
        onNext={onNext}
      />
    </>
  );
}
